// AI vehicle insights for the details screen.
//
// The prompts and the OpenAI call now run on the backend (POST /ai/insights).
// The browser sends only the car-data block and receives the raw model JSON,
// which it parses here. This keeps the OpenAI key out of the shipped bundle —
// previously VITE_OPENAI_API_KEY was inlined into the JS and extractable by
// anyone visiting the site. See car-details-proxy/clients/ai_insights_client.py.
//
// Two prompts share one car-data block:
//
// 1. Summary (fetchAiSummary): description + acceleration estimates. Deferred:
//    fired only after /car-data succeeds with a make + model, and re-fetched
//    when the app language changes (the prose is language-specific).
// 2. Fuel economy (fetchAiFuelEconomy): numeric l/100km estimates, requested
//    in realtime only for the figures that BOTH soov /car-data and the mnt
//    registry are missing (see useVehicleLookup, which waits for both sources
//    to settle before deciding). Numbers are language-independent, so this is
//    fetched once per plate and cached without a language key.

import { ApiError, detailsApiToken } from './http';
import { secureFetchJson } from '../security/secureFetch';
import type { CarDataResponse } from './carData';
import type { MntResponse } from './checkVehicle';

export interface AiSummaryInput {
  make: string;
  model: string;
  year: string | null;
  engineCapacityCc: string | null;
  enginePowerKw: string | null;
  curbWeightKg: string | null;
  /** Raw fuel type (often an Estonian registry value, e.g. "diisel"). */
  fuelType: string | null;
}

export interface AiCarInsights {
  description: string;
  zeroTo100Seconds: string | null;
  /** 100-200 km/h estimate; null when the car can't realistically do it. */
  hundredTo200Seconds: string | null;
}

/** Estimated fuel economy figures (bare l/100km numbers as strings). */
export interface AiFuelFigures {
  combined: string | null;
  city: string | null;
  highway: string | null;
}

/** Which fuel figures to ask for — only the ones both data sources lack. */
export interface AiFuelNeed {
  combined: boolean;
  city: boolean;
  highway: boolean;
}

/**
 * Whether to attempt the AI cards at all. The prompts/key live server-side now,
 * so the client can't inspect the key; the feature is on unless explicitly
 * disabled at build time, and a backend without OPENAI_API_KEY simply 503s and
 * the cards degrade gracefully.
 */
export function aiSummaryConfigured(): boolean {
  return import.meta.env.VITE_AI_INSIGHTS_ENABLED !== 'false';
}

// First number in the string, tolerating thousands spaces; a plain \D strip
// would swallow unit digits ("1968cm3" → "19683").
function digits(value: string | null | undefined): string | null {
  const match = value?.match(/\d[\d\s]*/)?.[0]?.replace(/\s/g, '') ?? '';
  return match.length > 0 ? match : null;
}

function numberString(value: number | null | undefined): string | null {
  return value === null || value === undefined || Number.isNaN(value) ? null : String(value);
}

/** Builds the AI prompt input; null when car-data lacks make/model. */
export function aiInputFromCarData(data: CarDataResponse): AiSummaryInput | null {
  const make = data.dynamic?.make?.trim();
  const model = data.dynamic?.model?.trim();
  if (!make || !model) return null;
  return {
    make,
    model,
    year: data.dynamic?.registreerimisaasta?.trim() || null,
    engineCapacityCc: digits(data.static?.MOOTORI_MAHT?.value),
    enginePowerKw: digits(data.dynamic?.voimsus),
    curbWeightKg: digits(data.static?.TYHIMASS?.value),
    fuelType: data.dynamic?.kutuse_tuup?.trim() || null,
  };
}

/**
 * Fallback prompt input from the mnt registry document, for when /car-data
 * came back empty but the registry still identified the vehicle.
 */
export function aiInputFromMnt(response: MntResponse): AiSummaryInput | null {
  if (response.status !== 'ok') return null;
  const vehicle = response.vehicle;
  const [make, ...modelParts] = vehicle.identification.make_model?.trim().split(/\s+/) ?? [];
  const model = modelParts.join(' ');
  if (!make || !model) return null;
  const summary = vehicle.summary;
  const tech = vehicle.technical_details;
  return {
    make,
    model,
    year:
      summary.first_registration.iso?.slice(0, 4) ??
      summary.first_registration.raw?.match(/\b\d{4}\b/)?.[0] ??
      null,
    engineCapacityCc: numberString(summary.engine_displacement_cc ?? tech.engine.displacement_cc),
    enginePowerKw: numberString(summary.engine_power_kw ?? tech.engine.power_kw),
    curbWeightKg: numberString(tech.masses.kerb_weight_kg),
    fuelType: summary.fuel ?? tech.engine.fuel ?? null,
  };
}

interface InsightsResponse {
  content?: string;
}

/**
 * POST the car-data block to the backend AI proxy and return the raw model
 * content (a JSON string the parsers below decode). The backend holds the key
 * and builds the prompt; the browser never sees either.
 */
async function postInsights(
  body: Record<string, unknown>,
  label: string,
  signal?: AbortSignal,
): Promise<string> {
  const response = await secureFetchJson<InsightsResponse>('/ai/insights', {
    method: 'POST',
    body,
    ...(signal ? { signal } : {}),
    timeoutMs: 60_000,
    label,
    legacyToken: detailsApiToken(),
  });
  const content = response.content?.trim();
  if (!content) throw new ApiError(`${label}: empty response`, null);
  return content;
}

export async function fetchAiSummary(
  input: AiSummaryInput,
  languageTag: string,
  options: { signal?: AbortSignal } = {},
): Promise<AiCarInsights> {
  const content = await postInsights(
    { kind: 'summary', input, language: languageTag },
    'AI summary',
    options.signal,
  );
  return parseAiInsights(content);
}

/**
 * Realtime fuel-economy estimate for the figures missing from both data
 * sources. Numbers only, so no language parameter.
 */
export async function fetchAiFuelEconomy(
  input: AiSummaryInput,
  need: AiFuelNeed,
  options: { signal?: AbortSignal } = {},
): Promise<AiFuelFigures> {
  const content = await postInsights({ kind: 'fuel', input, need }, 'AI fuel economy', options.signal);
  return parseAiFuelFigures(content);
}

/** Tolerates a ```json fence around the payload. */
function parseJsonPayload(content: string, label: string): unknown {
  const cleaned = content.replace(/```json/g, '').replace(/```/g, '').trim();
  try {
    return JSON.parse(cleaned);
  } catch {
    throw new ApiError(`${label}: invalid JSON in response`, null);
  }
}

/** Exported for tests. */
export function parseAiInsights(content: string): AiCarInsights {
  const record = parseJsonPayload(content, 'AI summary') as {
    description?: unknown;
    zero_to_100_kmh_seconds?: unknown;
    hundred_to_200_kmh_seconds?: unknown;
  };
  const description = typeof record.description === 'string' ? record.description.trim() : '';
  if (!description) throw new ApiError('AI summary: empty description', null);
  return {
    description,
    zeroTo100Seconds: numericString(record.zero_to_100_kmh_seconds),
    hundredTo200Seconds: numericString(record.hundred_to_200_kmh_seconds),
  };
}

/** Exported for tests. */
export function parseAiFuelFigures(content: string): AiFuelFigures {
  const record = parseJsonPayload(content, 'AI fuel economy') as {
    fuel_l_100km_combined?: unknown;
    fuel_l_100km_city?: unknown;
    fuel_l_100km_highway?: unknown;
  };
  return {
    combined: numericString(record.fuel_l_100km_combined),
    city: numericString(record.fuel_l_100km_city),
    highway: numericString(record.fuel_l_100km_highway),
  };
}

/**
 * A string/number field coerced to a trimmed string, or null when absent/empty
 * or non-numeric. The strict json_schema fields are typed ["string","null"], and
 * the model often emits the literal text "null" (or "n/a", "unknown") instead of
 * a real JSON null — so require at least one digit; "8.4" and "18-20" pass,
 * "null" becomes null and the figure is hidden rather than shown as text.
 */
function numericString(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed && /\d/.test(trimmed)) return trimmed;
  }
  return null;
}
